/**
 * formsUi
 *
 * Shared form copy used across CRA forms (Cancel/Submit/Save labels,
 * generic validation defaults, "An unexpected error occurred" fallbacks).
 *
 * Mirrors the existing CRA i18n adapter pattern (see authUi.ts, taskUi.ts):
 * a static `Record<Locale, Strings>` map plus a `resolveFormsLocale()` helper
 * that reads `navigator.language`. New keys should be added to all three
 * locale branches (`en-US`, `ja-JP`, `ko-KR`) in the same change.
 *
 * See `docs/architecture/forms-standard.md` section 5 for the localization
 * contract.
 */

export type FormsLocale = "en-US" | "ja-JP" | "ko-KR";

export interface FormsUiStrings {
  /** Default Cancel button label. */
  cancel: string;
  /** Default Submit button label. */
  submit: string;
  /** Default Save button label. */
  save: string;
  /** Default "Submitting..." progress label. */
  submitting: string;
  /** Default "Saving..." progress label. */
  saving: string;
  /** Required-field marker (used after labels). */
  requiredMarker: string;
  /** Required-field validation message (no specific field given). */
  requiredField: string;
  /** Generic validation failure (no specific message). */
  validationFailed: string;
  /** Generic unexpected error fallback (network / unknown). */
  unexpectedError: string;
  /** Default "Try again" button label for error states. */
  tryAgain: string;
  /** Default "Done" button label for success/inline states. */
  done: string;
  /** Default "Back" button label for multi-step flows. */
  back: string;
  /** Generic "Discard changes?" confirmation prompt. */
  discardChangesPrompt: string;
  /** Generic confirm button for "Discard". */
  discardChangesConfirm: string;
  /** Generic cancel button for "Keep editing". */
  discardChangesCancel: string;
}

const STRINGS: Record<FormsLocale, FormsUiStrings> = {
  "en-US": {
    cancel: "Cancel",
    submit: "Submit",
    save: "Save",
    submitting: "Submitting...",
    saving: "Saving...",
    requiredMarker: "*",
    requiredField: "This field is required",
    validationFailed: "Please review the highlighted fields",
    unexpectedError: "Something went wrong. Please try again.",
    tryAgain: "Try again",
    done: "Done",
    back: "Back",
    discardChangesPrompt: "Discard your changes?",
    discardChangesConfirm: "Discard",
    discardChangesCancel: "Keep editing",
  },
  "ja-JP": {
    cancel: "キャンセル",
    submit: "送信",
    save: "保存",
    submitting: "送信中...",
    saving: "保存中...",
    requiredMarker: "*",
    requiredField: "この項目は必須です",
    validationFailed: "入力内容をご確認ください",
    unexpectedError: "問題が発生しました。もう一度お試しください。",
    tryAgain: "もう一度試す",
    done: "完了",
    back: "戻る",
    discardChangesPrompt: "変更を破棄しますか？",
    discardChangesConfirm: "破棄する",
    discardChangesCancel: "編集を続ける",
  },
  "ko-KR": {
    cancel: "취소",
    submit: "제출",
    save: "저장",
    submitting: "제출 중...",
    saving: "저장 중...",
    requiredMarker: "*",
    requiredField: "필수 입력 항목입니다",
    validationFailed: "입력한 항목을 확인해 주세요",
    unexpectedError: "문제가 발생했습니다. 다시 시도해 주세요.",
    tryAgain: "다시 시도",
    done: "완료",
    back: "뒤로",
    discardChangesPrompt: "변경 사항을 취소하시겠습니까?",
    discardChangesConfirm: "취소",
    discardChangesCancel: "편집 계속",
  },
};

/**
 * Resolve the active forms locale from the browser. Falls back to en-US
 * during SSR / when `navigator.language` is unavailable.
 */
export function resolveFormsLocale(): FormsLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getFormsUiStrings(locale?: FormsLocale): FormsUiStrings {
  const key = locale ?? resolveFormsLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}
